import styles from './testCaseIncomingModal.module.scss';
import { useTranslation } from "react-i18next";
import { Input } from "../../shared/components/input/input";
import {
  Dropdown,
  DropdownItem
} from "../../shared/components/dropdown/dropdown";
import {
  useCallback,
  useEffect,
  useMemo,
  useState
} from "react";
import {
  useForm
} from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  number,
  object,
  string
} from "yup";
import { MessageWindow } from "../../shared/components/messageWindow/messageWindow";
import { useGetAllSectionsQuery } from "../../api/testrailService";
import {
  useDispatch,
  useSelector
} from "react-redux";
import {
  AppDispatch,
  RootState
} from "../../store";
import { Checkbox } from "../../shared/components/checkbox/checkbox";
import { MethodType } from "../../shared/components/methodType/methodType";
import { ParametersInput } from "../../shared/components/parametersInput/parametersInput";
import { TestCaseIncoming } from "../../types/testCaseIncomingTypes";
import { setUpdateModal } from "../../store/slices/mainSlice";
import {
  useCreateTestCaseIncomingMutation,
  useDeleteTestCaseIncomingMutation,
  useUpdateTestCaseIncomingMutation
} from "../../api/testCaseIncomingService";
import { ModalWindow } from "../../shared/components/modalWindow/modalWindow";
import { TextArea } from "../../shared/components/textArea/textArea";

type TestCaseIncomingModalProps = {
  onClose: (isSave: boolean, idCreate?: number) => void;
  testCaseIncoming?: TestCaseIncoming
}

export const TestCaseIncomingModal = ({ onClose, testCaseIncoming }: TestCaseIncomingModalProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();

  const methods = useSelector((state: RootState) => state.mainSlice.methods);

  const [showDeleteMessage, setShowDeleteMessage] = useState<boolean>();

  const [showConfirmCancel, setShowConfirmCancel] = useState(false);

  const [exportTestrail, setExportTestrail] = useState<boolean>(!!testCaseIncoming?.sectionId)

  const { data: sections} = useGetAllSectionsQuery("", { skip: !exportTestrail, refetchOnMountOrArgChange: true });

  const [createTestCaseIncoming, { data: createData, isSuccess: isSuccessCreateTestCaseIncoming, isLoading: isLoadingCreate }] = useCreateTestCaseIncomingMutation();
  const [updateTestCaseIncoming, { isSuccess: isSuccessUpdateTestCaseIncoming, isLoading: isLoadingUpdate }] = useUpdateTestCaseIncomingMutation();
  const [deleteTestCaseIncoming, { isSuccess: isSuccessDeleteTestCaseIncoming, isLoading: isLoadingDelete }] = useDeleteTestCaseIncomingMutation();

  useEffect(() => {
    if (isSuccessCreateTestCaseIncoming && !!createData) {
      dispatch(setUpdateModal({ show: true, isDelete: false }));
      onClose(true, createData);
    }
  }, [isSuccessCreateTestCaseIncoming, createData]);

  useEffect(() => {
    if (isSuccessUpdateTestCaseIncoming) {
      dispatch(setUpdateModal({ show: true, isDelete: false }));
      onClose(true);
    }
  }, [isSuccessUpdateTestCaseIncoming]);

  useEffect(() => {
    if (isSuccessDeleteTestCaseIncoming) {
      dispatch(setUpdateModal({ show: true, isDelete: true }));
      onClose(true);
    }
  }, [isSuccessDeleteTestCaseIncoming]);

  const schema = object({
    name: string().typeError(t("common.notValid")).required(t("common.notValid")),
    methodId: number().required(),
    sectionId: number().nullable(),
    description: string(),
    endpoint: string(),
    request: string(),
    response: string(),
  });

  const {
    handleSubmit,
    formState: { errors, isDirty },
    control
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      name: testCaseIncoming?.name || "",
      description: testCaseIncoming?.description || "",
      endpoint: testCaseIncoming?.endpoint || "",
      methodId: testCaseIncoming?.methodId || methods[0].id,
      sectionId: testCaseIncoming?.sectionId || 0,
      request: testCaseIncoming?.request || "",
      response: testCaseIncoming?.response || ""
    },
    mode: "onChange"
  });

  const onCancel = useCallback(() => {
    if (isDirty) {
      setShowConfirmCancel(true);
    } else {
      onClose(false);
    }
  }, [isDirty, onClose]);

  const closeConfirmCancelModal = useCallback((isConfirm: boolean) => {
    if (isConfirm) {
      onClose(false);
    }
    setShowConfirmCancel(false);
  }, [setShowConfirmCancel]);

  const onClickSave = (): void => {
    handleSubmit((value) => {
      let sectionId = exportTestrail && !!value.sectionId ? value.sectionId : undefined;
      const newTestCaseIncoming: TestCaseIncoming = {
        name: value.name,
        methodId: value.methodId,
        sectionId,
        testRailTestCaseId: testCaseIncoming?.testRailTestCaseId,
        description: value.description,
        endpoint: value.endpoint,
        request: value.request,
        response: value.response,
      };
      if (!!testCaseIncoming?.id) {
        newTestCaseIncoming.id = testCaseIncoming.id;
        updateTestCaseIncoming(newTestCaseIncoming);
      } else {
        createTestCaseIncoming(newTestCaseIncoming);
      }
    })();
  }

  const onDeleteClick = (): void => {
    setShowDeleteMessage(true);
  }

  const closeDeleteMessageWindow = (isConfirm: boolean): void => {
    if (isConfirm && !!testCaseIncoming?.id) {
      deleteTestCaseIncoming(testCaseIncoming.id);
    }
    setShowDeleteMessage(false);
  }

  const onChangeExportTestrail = () => {
    if (!disabledExportCheckbox()) {
      setExportTestrail(!exportTestrail);
    }
  }

  const disabledExportCheckbox = (): boolean =>
    !!testCaseIncoming?.id && !!testCaseIncoming.sectionId;

  const getMethodsDropdownItems: DropdownItem[] = useMemo(() => {
    return methods.map(item => ({
      id: item.id,
      name: <MethodType methodId={item.id}/>,
      title: item.name
    }));
  }, [methods]);

  const getSectionDropdownItems: DropdownItem[] = useMemo(() => {
    if (!!sections && exportTestrail) {
      return sections.map(item => ({
        id: item.id,
        name: item.name,
        title: item.name
      }))
    } else {
      return []
    }
  }, [sections, exportTestrail]);

  return (
    <>
      <ModalWindow title={!!testCaseIncoming?.id ? t("testCaseIncoming.edit") : t("testCaseIncoming.add")}
                   onClose={onCancel}
                   width={814}
                   children={
                     <>
                       <div className={styles.container}>
                         <div className={styles.inputs}>
                           <div className={styles.inputContainer}>
                             <span className={styles.required}>{t("testCaseIncoming.name")}</span>
                             <div className={styles.input}>
                               <Input control={control}
                                      name={"name"}
                                      maxLength={100}
                                      error={!!errors.name ? errors.name.message : ""}/>
                             </div>
                           </div>
                           <div className={styles.checkbox}
                                onClick={onChangeExportTestrail}>
                             <Checkbox isChecked={exportTestrail} disabled={disabledExportCheckbox()}/>
                             <span>{t("testCase.exportToTestRail")}</span>
                           </div>
                         </div>
                         <div className={styles.inputs}>
                           <div className={styles.inputContainer}>
                             <span>{t("testCase.method")}</span>
                             <div className={styles.input}>
                               <Dropdown control={control}
                                         fontPopupClass={styles.fontDropdownPopup}
                                         items={getMethodsDropdownItems}
                                         name={"methodId"}/>
                             </div>
                           </div>
                           <div className={styles.inputContainer}>
                             <span>{t("testCase.section")}</span>
                             <div className={styles.input}>
                               <Dropdown control={control}
                                         fontPopupClass={styles.fontDropdownPopup}
                                         items={getSectionDropdownItems}
                                         name={"sectionId"}
                                         disabled={!exportTestrail}/>
                             </div>
                           </div>
                         </div>
                         <div className={`${styles.textAreaContainer} ${styles.description}`}>
                           <div className={styles.title}>
                             {t("common.description")}
                           </div>
                           <TextArea control={control}
                                     maxLength={57000}
                                     name={"description"}/>
                         </div>
                         <div className={styles.endpoint}>
                           <ParametersInput control={control}
                                            title={t("testCase.endpoint")}
                                            name={"endpoint"}
                                            maxLength={255}
                                            error={!!errors.endpoint ? errors.endpoint.message : ""}/>
                         </div>
                         <div className={styles.textAreaContainer}>
                           <ParametersInput control={control}
                                            showConstantsParameters={true}
                                            title={t("testCase.request")}
                                            name={"request"}
                                            maxLength={65535}/>
                         </div>
                         <div className={styles.textAreaContainer}>
                           <ParametersInput control={control}
                                            title={t("testCase.response")}
                                            name={"response"}
                                            maxLength={65535}/>
                         </div>
                       </div>
                       <div className={styles.buttonsContainer}>
                         <div className={styles.buttons}>
                           {!!testCaseIncoming?.id && (
                             <button className={styles.delete}
                                     disabled={isLoadingCreate || isLoadingDelete || isLoadingUpdate}
                                     onClick={onDeleteClick}>{t("common.delete")}</button>
                           )}
                         </div>
                         <div className={styles.buttons}>
                           <button className={styles.cancel}
                                   disabled={isLoadingCreate || isLoadingDelete || isLoadingUpdate}
                                   onClick={onCancel}>{t("common.cancel")}</button>
                           <button className={styles.save}
                                   disabled={isLoadingCreate || isLoadingDelete || isLoadingUpdate}
                                   onClick={onClickSave}>{t("common.save")}</button>
                         </div>
                       </div>
                     </>
                   }
      />

      {showDeleteMessage && (
        <MessageWindow onClose={closeDeleteMessageWindow}
                       title={t("testCase.delete")}
                       description={t("testCase.deleteDescription")}
                       primaryText={t("common.ok")} secondaryText={t("common.cancel")}/>
      )}

      {showConfirmCancel && (
        <MessageWindow onClose={closeConfirmCancelModal}
                       title={t("common.cancelChanges")}
                       description={t("common.cancelChangesDescription")}
                       primaryText={t("common.ok")} secondaryText={t("common.cancel")}/>
      )}
    </>
  );
}